/**
 * CustomTooltip - Chart Tooltip React Component
 * 
 * SOLID Principles: Single Responsibility Principle (SRP) & Interface Segregation Principle (ISP)
 * Purpose: This component is solely responsible for rendering chart tooltips.
 * It focuses only on UI presentation, delegating all formatting logic to
 * the TooltipFormatter service.
 * 
 * Responsibilities:
 * - Render tooltip UI with proper styling
 * - Display formatted timestamp
 * - Show multiple sensor values in organized layout
 * - Handle visual indicators for data quality
 * 
 * Context: Appears when users hover over chart data points. Shows timestamp
 * and all sensor values at that point in time. Needs to be highly readable
 * and handle cases with many simultaneous sensor readings.
 */

import React from 'react';
import { TooltipFormatter } from './TooltipFormatter.js';
import { LabelUtils } from '../LabelUtils.js';

/**
 * Custom tooltip component for sensor data visualization
 * 
 * Integrates with Recharts tooltip system but provides custom formatting
 * and styling appropriate for marine sensor data display.
 * 
 * @param {Object} props - Recharts tooltip props
 * @param {boolean} props.active - Whether tooltip is currently shown
 * @param {Array} props.payload - Array of data points at current position
 * @param {string} props.label - Timestamp for current position
 */
export function CustomTooltip({ active, payload, label }) {
  // Don't render anything if tooltip is inactive or has no data
  if (!active || !payload || !payload.length) {
    return null;
  }

  const formattedTimestamp = TooltipFormatter.formatTimestamp(label);

  return (
    <TooltipContainer>
      <TimestampHeader>
        {formattedTimestamp}
      </TimestampHeader>
      
      <ValuesList>
        {payload.map((entry, index) => (
          <TooltipEntry
            key={index}
            entry={entry}
          />
        ))}
      </ValuesList>
    </TooltipContainer>
  );
}

/**
 * Individual tooltip entry for a single sensor value
 * 
 * @param {Object} props
 * @param {Object} props.entry - Single data entry from Recharts
 */
function TooltipEntry({ entry }) {
  const [formattedValue] = TooltipFormatter.formatTooltipValue(
    entry.value, 
    entry.dataKey, 
    entry.payload
  );

  const fieldLabel = LabelUtils.getFieldLabel(entry.dataKey);
  const isSuspicious = TooltipFormatter.isValueSuspicious(entry.value, entry.dataKey);

  return (
    <ValueEntry style={{ color: entry.color }}>
      <ValueLabel>
        {fieldLabel}:
      </ValueLabel>
      <ValueDisplay>
        {formattedValue}
        {isSuspicious && (
          <SuspiciousValueIndicator 
            title={TooltipFormatter.getSuspiciousValueWarning(entry.value, entry.dataKey)}
          >
            ⚠️
          </SuspiciousValueIndicator>
        )}
      </ValueDisplay>
    </ValueEntry>
  );
}

/**
 * Main tooltip container with styling and positioning
 */
function TooltipContainer({ children }) {
  return (
    <div style={{
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      border: '1px solid #ccc',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      fontSize: '0.9rem',
      padding: '12px',
      maxWidth: '300px',
      wordWrap: 'break-word'
    }}>
      {children}
    </div>
  );
}

/**
 * Timestamp header styling
 */
function TimestampHeader({ children }) {
  return (
    <div style={{ 
      fontWeight: 'bold', 
      marginBottom: '8px',
      borderBottom: '1px solid #eee',
      paddingBottom: '4px'
    }}>
      {children}
    </div>
  );
}

/**
 * Container for list of sensor values
 */
function ValuesList({ children }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    }}>
      {children}
    </div>
  );
}

/**
 * Individual value entry with sensor name and formatted value
 */
function ValueEntry({ children, style }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      ...style
    }}>
      {children}
    </div>
  );
}

/**
 * Sensor name label
 */
function ValueLabel({ children }) {
  return (
    <span style={{
      fontWeight: '500',
      marginRight: '8px',
      flex: '1 1 auto'
    }}>
      {children}
    </span>
  );
}

/**
 * Formatted value display
 */
function ValueDisplay({ children }) {
  return (
    <span style={{
      fontFamily: 'monospace',
      fontWeight: 'bold',
      flex: '0 0 auto',
      textAlign: 'right'
    }}>
      {children}
    </span>
  );
}

/**
 * Warning indicator for suspicious values
 */
function SuspiciousValueIndicator({ children, title }) {
  return (
    <span 
      style={{
        marginLeft: '4px',
        fontSize: '0.8em',
        cursor: 'help'
      }}
      title={title}
    >
      {children}
    </span>
  );
}

export default CustomTooltip;